<script lang="ts" setup>
import moment from "moment";
import { computed, onMounted, onUnmounted, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import DatePicker from "~/components/dumb/DatePicker.vue";
import TimePicker from "~/components/dumb/TimePicker.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { ArrowRightIcon, XIcon } from "~/icons";
import { ButtonStyle, EditorMode } from "~/shared/enums";
import type { TimeTrackingEntry as ITimeTrackingEntry } from "~/shared/types";
import { combineDateAndTime, getDurationShortText, useTimeTracker } from "~/utils/time";

const props = defineProps<{
  entry: ITimeTrackingEntry;
}>();

const emit = defineEmits<{
  update: [current: ITimeTrackingEntry, updated: ITimeTrackingEntry];
  delete: [entry: ITimeTrackingEntry];
}>();

const activeTimeEntry = computed(() => (props.entry.finishedAt ? undefined : props.entry));
const { now, startTimer, stopTimer } = useTimeTracker(activeTimeEntry);

const selectedStartDate = computed(() => moment(props.entry.startedAt).toISOString());
const selectedFinishDate = computed(() =>
  (props.entry.finishedAt ? moment(props.entry.finishedAt) : moment(now.value)).toISOString()
);

const showFinishedDate = computed(
  () =>
    props.entry.finishedAt &&
    moment(props.entry.startedAt).format("YYYY-MM-DD") !== moment(props.entry.finishedAt).format("YYYY-MM-DD")
);

const changeDate = (date: string | null, isStart: boolean, isTime = false) => {
  if (!date || moment(date).isSame(moment(now.value))) {
    return;
  }

  const entry = { ...props.entry };
  if (isStart) {
    entry.startedAt = combineDateAndTime(entry.startedAt, date, isTime);
  } else {
    entry.finishedAt = combineDateAndTime(entry.finishedAt ?? date, date, isTime);
  }

  const newDiff = moment(entry.finishedAt).diff(moment(entry.startedAt));
  if (newDiff > 0) {
    emit("update", props.entry, entry);
    return;
  }

  if (isStart) {
    entry.finishedAt = moment(entry.startedAt).add(1, "hour").toISOString();
  } else {
    entry.startedAt = moment(entry.finishedAt).subtract(1, "hour").toISOString();
  }
  emit("update", props.entry, entry);
};

watch(
  () => activeTimeEntry.value,
  (newValue) => {
    if (!newValue) {
      return;
    }
    startTimer();
  }
);

onMounted(() => {
  if (!activeTimeEntry.value) {
    return;
  }
  startTimer();
});

onUnmounted(() => {
  stopTimer();
});
</script>

<template>
  <tr
    class="group/time-tracking relative grid grid-cols-[60px_1fr_auto] border-md py-0.5 pr-3 pl-2 text-lt hover:bg-md">
    <td class="flex items-center truncate">
      <DatePicker
        block
        :value="entry.startedAt"
        :editor-mode="EditorMode.DETAIL"
        :max-start-date="new Date()"
        hide-actions
        prevent-unset
        @select="(newDate) => changeDate(newDate, true)">
        <Button
          :text="moment(entry.startedAt).format('MMM D')"
          :btn-style="ButtonStyle.CHIP"
          text-style="text-sm justify-start !text-left grow"
          is-contrast
          borderless />
      </DatePicker>
    </td>

    <td class="flex items-center">
      <TimePicker :value="selectedStartDate" @select="(newValue: string) => changeDate(newValue, true, true)" />
      <ArrowRightIcon class="size-3 shrink-0" />
      <TimePicker :value="selectedFinishDate" @select="(newValue: string) => changeDate(newValue, false, true)" />
      <div v-if="showFinishedDate" class="ml-2">
        <DatePicker
          block
          :value="entry.finishedAt"
          :editor-mode="EditorMode.DETAIL"
          :max-start-date="new Date()"
          hide-actions
          @select="(newDate) => changeDate(newDate, false)">
          <Button
            :text="moment(entry.finishedAt).format('MMM D')"
            :btn-style="ButtonStyle.CHIP"
            text-style="text-sm justify-start !text-left grow"
            is-contrast
            borderless />
        </DatePicker>
      </div>
    </td>

    <td class="flex items-center gap-1">
      <span class="text-sm tabular-nums select-none">
        {{ getDurationShortText(entry.startedAt, entry.finishedAt ?? new Date().toISOString()) }}
      </span>

      <div class="absolute -top-1.5 right-0 hidden h-1.5 w-3 group-hover/time-tracking:flex" />
      <button
        type="button"
        class="absolute -top-1.5 right-0 hidden icon-xs cursor-pointer items-center justify-center rounded-full border border-md bg-lt group-hover/time-tracking:flex"
        aria-label="Delete entry"
        @click.stop="emit('delete', entry)"
        @keydown.enter.stop="emit('delete', entry)">
        <Tooltip text="Delete entry">
          <XIcon class="size-full text-lt" />
        </Tooltip>
      </button>
    </td>
  </tr>
</template>
